// Data loading hook for the My Feed Reader page. Owns subscriptions,
// items, unread counts, search/filter state, and refresh orchestration.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  type FeedFilter,
  type FeedItem,
  type FeedSubscription,
  fetchSubscriptions,
  fetchUnreadCounts,
  fetchFeedItems,
  fetchTodayExtractionCount,
  prefetchExtraction,
} from "@/lib/feedReader";

type Args = {
  scopeIds: string[];
  wsLoading: boolean;
  activeWorkspaceId: string | null;
};

export function useFeedReaderData({ scopeIds, wsLoading, activeWorkspaceId }: Args) {
  const [subscriptions, setSubscriptions] = useState<FeedSubscription[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [items, setItems] = useState<FeedItem[]>([]);
  const [filter, setFilter] = useState<FeedFilter>("unread");
  const [selectedSub, setSelectedSub] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [extractionUsed, setExtractionUsed] = useState(0);
  const itemRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Debounce the search input.
  useEffect(() => {
    const t = window.setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const refresh = useCallback(async () => {
    if (!scopeIds.length) {
      setSubscriptions([]);
      setItems([]);
      setUnreadCounts({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [subs, fetchedItems] = await Promise.all([
        fetchSubscriptions(scopeIds),
        fetchFeedItems({ workspaceIds: scopeIds, filter, subscriptionId: selectedSub }),
      ]);
      setSubscriptions(subs);
      setItems(fetchedItems);
      const counts = await fetchUnreadCounts(subs.map((s) => s.id));
      setUnreadCounts(counts);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scopeIds, filter, selectedSub]);

  useEffect(() => {
    if (!wsLoading) refresh();
  }, [refresh, wsLoading]);

  // Today's per-workspace article-extraction usage (for pre-block warning).
  const refreshExtractionUsage = useCallback(async () => {
    if (!activeWorkspaceId) {
      setExtractionUsed(0);
      return;
    }
    try {
      setExtractionUsed(await fetchTodayExtractionCount(activeWorkspaceId));
    } catch {
      /* non-fatal */
    }
  }, [activeWorkspaceId]);
  useEffect(() => { void refreshExtractionUsage(); }, [refreshExtractionUsage]);

  const subById = useMemo(() => {
    const m = new Map<string, FeedSubscription>();
    for (const s of subscriptions) m.set(s.id, s);
    return m;
  }, [subscriptions]);

  // Client-side search across loaded items.
  const visibleItems = useMemo(() => {
    if (!searchQuery) return items;
    return items.filter((it) => {
      const hay = `${it.title ?? ""} ${it.summary ?? ""} ${it.author ?? ""}`.toLowerCase();
      return hay.includes(searchQuery);
    });
  }, [items, searchQuery]);

  const selectedItem = useMemo(
    () => visibleItems.find((it) => it.id === selectedItemId) ?? null,
    [visibleItems, selectedItemId],
  );

  // Keep selection valid when the visible list changes.
  useEffect(() => {
    if (selectedItemId && !visibleItems.some((it) => it.id === selectedItemId)) {
      setSelectedItemId(null);
    }
  }, [visibleItems, selectedItemId]);

  // Auto-scroll the selected item into view inside the list pane.
  useEffect(() => {
    if (!selectedItemId) return;
    const el = itemRowRefs.current.get(selectedItemId);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedItemId]);

  // Warm the server-side extraction cache when the user keyboard-navigates.
  useEffect(() => {
    if (!selectedItem) return;
    const target = selectedItem;
    const t = window.setTimeout(() => prefetchExtraction(target), 150);
    return () => window.clearTimeout(t);
  }, [selectedItem]);

  return {
    subscriptions, items, setItems, unreadCounts, setUnreadCounts,
    filter, setFilter, selectedSub, setSelectedSub,
    loading, selectedItemId, setSelectedItemId,
    searchInput, setSearchInput, searchQuery,
    extractionUsed, setExtractionUsed, refreshExtractionUsage,
    itemRowRefs, refresh, subById, visibleItems, selectedItem,
  };
}
